import { css } from '@emotion/react';
import {
  ProductRating,
  RedirectToMusicDistributionDeliveryButton,
  useProductPage,
} from 'components';
import { Body, Button, Card, H2, H3 } from 'imdui';
import { useTranslation } from 'react-i18next';
import { ProductScreen, ThankYouGrid } from '../../shared';
import { useEffect } from 'react';
import type { EntityModels } from 'imddata';
import { useEntries, useTracks } from 'imddata';
import { MasteringTrackAudio } from './MasteringTrackAudio';
import { useHistory } from 'react-router';
import { defaultFileUrlContextValue } from 'imdshared';

export const MasteringThankYouPage = () => {
  const { reload, entries: tracks } = useTracks();
  const { entries: masteringTracks } = useEntries<EntityModels.MasteringTrack>({
    entity: 'masteringTracks',
  });

  const { t } = useTranslation();

  const productState = useProductPage();
  useEffect(() => {
    const intervalId = setInterval(reload, 5000); // Refresh every 5 seconds
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [reload]);

  const history = useHistory();
  const masteredTrackIds = (productState.state.data
    .lastOrderedMasteredTrackIds || []) as (string | number)[];

  const resultTracks = masteredTrackIds
    .map((mti) => {
      const masteringTrack = masteringTracks.find((mt) => mt.id === mti);
      if (!masteringTrack) return null;
      const track = tracks.find((t) => {
        return (
          t.states?.mastering?.masteringReferenceAudioId ===
            masteringTrack?.masteringReferenceAudioId &&
          t.states.mastering.originalTrackId == masteringTrack.trackId
        );
      });
      return {
        track,
        masteringTrack,
      };
    })
    .filter(Boolean);

  return (
    <ProductScreen title={t('instant-mastering')}>
      <ThankYouGrid
        css={css`
          margin: 16px 0;
        `}
      >
        <div>
          <Card
            css={css`
              padding: 32px;
              gap: 32px;
              display: grid;
            `}
          >
            <div
              css={css`
                display: grid;
                gap: 16px;
              `}
            >
              <H2>{t('thank-you-for-mastering')}</H2>
              <Body secondary>{t('thank-you-for-mastering-description')}</Body>
            </div>
            <div
              css={css`
                height: 1px;
                background: var(--outline-var, rgba(252, 252, 252, 0.1));
              `}
            />
            <div>
              <div
                css={css`
                  display: flex;
                  margin-bottom: 24px;
                  justify-content: space-between;
                  align-items: flex-end;
                `}
              >
                <H3>{t('your-mastered-tracks')}</H3>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;
                  `}
                >
                  <Button
                    text={t('view-in-library')}
                    disabled={
                      !!resultTracks.find(
                        (rt) =>
                          !rt?.track || !defaultFileUrlContextValue(rt.track)
                      )
                    }
                    onClick={() => {
                      history.push('/library/tracks');
                    }}
                  />
                  <RedirectToMusicDistributionDeliveryButton
                    text={t('distribute')}
                    primary
                    tracks={
                      resultTracks
                        .map((rt) => rt?.track?.id)
                        .filter(Boolean) as number[]
                    }
                    disabled={!!resultTracks.find((rt) => !rt?.track)}
                  />
                </div>
              </div>
              <div css={css({ display: 'grid', gap: 16 })}>
                {resultTracks.map((t, idx) => (
                  <div
                    css={css({
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center',
                    })}
                    key={t!.masteringTrack.id}
                  >
                    <Body>{idx + 1}</Body>

                    <MasteringTrackAudio
                      primary={!!t?.track?.audioPreview?.file.downloadUrl}
                      trackId={t?.track?.id}
                      loading={!t?.track?.audioPreview?.file.downloadUrl}
                      title={t?.track?.title}
                      subtitle={t?.track?.states?.mastering?.label}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        <ProductRating product="instant-mastering" />
      </ThankYouGrid>
    </ProductScreen>
  );
};
